/**
 * Load an AudioWorklet module ONCE PER AudioContext, from its source text.
 *
 * WHY THIS IS SHARED. `registerProcessor` throws when a name is registered twice
 * on the same context, and the call's 48 kHz context now has two users of the
 * `pcm-tap` processor: the fallback recorder (which taps the assistant track on
 * every browser, see recorder.ts) and the live-audio taps a listen-in adds
 * (./live/audio.ts). Each used to cache its own load, so whichever came second
 * would have failed with "already registered" and lost its audio. One cache,
 * keyed by context and by source, makes the second caller wait for the first.
 *
 * FROM A BLOB, NEVER A URL ASSET. The same reason as the header of
 * workers/pcm-tap.js: on the embed path Vite emitted the asset url as a
 * root-relative string, which resolved against the customer's page and 404'd.
 *
 * A FAILED LOAD IS FORGOTTEN, so a later caller can try again. Nothing is
 * registered when `addModule` rejects, so a retry cannot hit the duplicate-name
 * error this exists to avoid.
 */
const loaded = new WeakMap<BaseAudioContext, Map<string, Promise<void>>>()

export function loadWorkletModule(ctx: BaseAudioContext, source: string): Promise<void> {
  let perContext = loaded.get(ctx)
  if (!perContext) {
    perContext = new Map()
    loaded.set(ctx, perContext)
  }
  const cached = perContext.get(source)
  if (cached) return cached
  const promise = (async () => {
    const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }))
    try {
      await ctx.audioWorklet.addModule(url)
    } finally {
      URL.revokeObjectURL(url)
    }
  })()
  perContext.set(source, promise)
  const map = perContext
  promise.catch(() => {
    if (map.get(source) === promise) map.delete(source)
  })
  return promise
}
